import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

/**
 * Las pestañas de Finanzas.
 *
 * Antes eran las pestañas de Bootstrap tal cual: cinco textos con una raya
 * debajo del activo. Esto las convierte en un control segmentado: una tarjeta
 * que se desliza por debajo de la pestaña abierta.
 *
 * Sin iconos a propósito. Lo que distingue a la pestaña abierta es el
 * movimiento y el relieve, no un símbolo más que leer.
 *
 * El indicador se mide, no se dibuja con CSS, porque cada pestaña tiene el
 * ancho de su texto y algunas llevan contador. Si la medición fallara, la
 * pestaña activa igual se distingue por su propio color: el indicador es un
 * adorno, no la única señal.
 *
 * Teclado: flechas para moverse entre pestañas, Inicio y Fin para ir a los
 * extremos. Es lo que espera un `tablist` y antes no estaba.
 */
const FinanceTabs = ({ sections, activeId, hrefFor, badgeFor }) => {
  const listRef = useRef(null);
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState(null);

  const measure = useCallback(() => {
    const active = tabRefs.current[activeId];
    const list = listRef.current;

    if (!active || !list) {
      setIndicator(null);
      return;
    }

    // offsetLeft es relativo al contenedor, asi que el indicador sigue en su
    // sitio aunque la barra este desplazada horizontalmente.
    setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
  }, [activeId]);

  useLayoutEffect(measure, [measure, sections.length]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    if (listRef.current) observer.observe(listRef.current);
    return () => observer.disconnect();
  }, [measure]);

  // La pestaña activa puede quedar fuera de la vista en un móvil, donde la
  // barra se desplaza. Al cambiar de sección se la trae al centro.
  useEffect(() => {
    const active = tabRefs.current[activeId];
    if (active?.scrollIntoView) {
      active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeId]);

  const onKeyDown = (event) => {
    const keys = ["ArrowRight", "ArrowLeft", "Home", "End"];
    if (!keys.includes(event.key)) return;

    event.preventDefault();
    const focused = Object.keys(tabRefs.current).find(
      (id) => tabRefs.current[id] === document.activeElement
    );
    const index = sections.findIndex((section) => section.id === (focused || activeId));
    const last = sections.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];

    tabRefs.current[sections[next].id]?.focus();
  };

  return (
    <div className="finance-tabs mb-4">
      <div
        className="finance-tabs__list"
        role="tablist"
        aria-label="Secciones de Finanzas"
        ref={listRef}
      >
        {indicator && (
          <span
            className="finance-tabs__indicator"
            aria-hidden="true"
            style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
          />
        )}

        {sections.map(({ id, label }) => {
          const isActive = id === activeId;
          const count = badgeFor(id);

          return (
            <Link
              key={id}
              to={hrefFor(id)}
              replace
              role="tab"
              id={`finance-tab-${id}`}
              aria-selected={isActive}
              aria-controls={`finance-panel-${id}`}
              tabIndex={isActive ? 0 : -1}
              ref={(node) => {
                tabRefs.current[id] = node;
              }}
              onKeyDown={onKeyDown}
              className={`finance-tabs__tab${isActive ? " is-active" : ""}`}
            >
              <span className="finance-tabs__label">{label}</span>
              {count > 0 && <span className="finance-tabs__count">{count}</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

FinanceTabs.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  activeId: PropTypes.string.isRequired,
  hrefFor: PropTypes.func.isRequired,
  badgeFor: PropTypes.func.isRequired,
};

export default FinanceTabs;
